import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactECharts from 'echarts-for-react';
import SafeIcon from '../common/SafeIcon';
import { FiTrendingUp, FiCheckCircle, FiClock, FiAlertCircle, FiTarget, FiCheck, FiX } from 'react-icons/fi';
import { mockStats, mockApprovals as initialApprovals } from '../data/mockData';

export default function Dashboard() {
  const [approvals, setApprovals] = useState(initialApprovals);
  const [activeRange, setActiveRange] = useState('7 Days');

  const handleApproval = (id, action) => {
    setApprovals(prev => prev.filter(item => item.id !== id));
  };

  const chartOptions = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      backgroundColor: '#141414',
      borderColor: '#2a2a2a',
      textStyle: { color: '#fff' }
    },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      axisLine: { lineStyle: { color: '#333' } }
    },
    yAxis: {
      type: 'value',
      splitLine: { lineStyle: { color: '#1a1a1a', type: 'dashed' } }
    },
    series: [
      {
        name: 'Engagement',
        type: 'line',
        smooth: true,
        lineStyle: { width: 3, color: '#FF5A5F' },
        areaStyle: {
          color: {
            type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [{ offset: 0, color: 'rgba(255,90,95,0.4)' }, { offset: 1, color: 'rgba(255,90,95,0)' }]
          }
        },
        data: activeRange === '7 Days' ? [120, 132, 101, 134, 190, 230, 210] : [450, 520, 480, 610, 590, 720, 680]
      }
    ]
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      className="p-8 max-w-7xl mx-auto h-full overflow-y-auto"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {[
          { label: 'Active Campaigns', value: mockStats.activeCampaigns, icon: FiTarget, color: 'text-blue-400' },
          { label: 'Posts Scheduled', value: mockStats.postsScheduled, icon: FiClock, color: 'text-green-400' },
          { label: 'Pending Approvals', value: approvals.length, icon: FiAlertCircle, color: 'text-yellow-400' },
          { label: 'Engagement Rate', value: mockStats.engagementRate, icon: FiTrendingUp, color: 'text-[#FF5A5F]' },
        ].map((stat, i) => (
          <motion.div 
            key={i} 
            whileHover={{ y: -5 }}
            className="glass-panel p-6 border-[#1a1a1a] hover:border-[#FF5A5F]/30 transition-colors"
          >
            <div className="flex justify-between items-start mb-4">
              <p className="text-gray-400 text-sm font-medium">{stat.label}</p>
              <SafeIcon icon={stat.icon} className={`text-xl ${stat.color}`} />
            </div>
            <h3 className="text-3xl font-bold text-white">{stat.value}</h3>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 glass-panel p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-medium text-white">Audience Engagement</h3>
            <select 
              value={activeRange}
              onChange={(e) => setActiveRange(e.target.value)}
              className="bg-[#141414] border border-[#2a2a2a] text-sm text-gray-300 rounded px-3 py-1 focus:outline-none focus:border-[#FF5A5F]"
            >
              <option>7 Days</option>
              <option>30 Days</option>
            </select>
          </div>
          <div className="h-72">
            <ReactECharts option={chartOptions} style={{ height: '100%', width: '100%' }} />
          </div>
        </div>

        <div className="glass-panel p-6 flex flex-col h-[400px]">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-medium text-white">Approval Flow</h3>
            <span className="bg-[#FF5A5F]/20 text-[#FF5A5F] text-xs px-2 py-1 rounded-full">{approvals.length} Pending</span>
          </div>
          
          <div className="space-y-4 flex-1 overflow-y-auto pr-2">
            <AnimatePresence mode="popLayout">
              {approvals.length > 0 ? (
                approvals.map((item) => (
                  <motion.div 
                    key={item.id}
                    layout
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="p-4 rounded-xl bg-[#141414] border border-[#2a2a2a] hover:border-[#FF5A5F]/50 transition-colors group"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="text-white text-sm font-medium">{item.title}</h4>
                      <span className="text-xs text-gray-500">{item.time}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-400 mb-4">
                      <span className="px-2 py-0.5 rounded bg-[#1a1a1a]">{item.channel}</span>
                      <span>•</span>
                      <span>{item.author}</span>
                    </div>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => handleApproval(item.id, 'approve')}
                        className="flex-1 flex items-center justify-center gap-2 bg-[#1a1a1a] hover:bg-green-500/20 hover:text-green-400 text-gray-300 py-1.5 rounded text-xs font-medium transition-colors border border-[#2a2a2a]"
                      >
                        <SafeIcon icon={FiCheck} size={12} /> Approve
                      </button>
                      <button 
                         onClick={() => handleApproval(item.id, 'reject')}
                        className="flex-1 flex items-center justify-center gap-2 bg-[#1a1a1a] hover:bg-red-500/20 hover:text-red-400 text-gray-300 py-1.5 rounded text-xs font-medium transition-colors border border-[#2a2a2a]"
                      >
                        <SafeIcon icon={FiX} size={12} /> Reject
                      </button>
                    </div>
                  </motion.div>
                ))
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-center opacity-50">
                  <SafeIcon icon={FiCheckCircle} className="text-4xl mb-3 text-green-500" />
                  <p className="text-sm text-gray-400">All caught up!</p>
                </div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </motion.div>
  );
}